import { notifications } from '@mantine/notifications';
import { createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks';
import i18n from '@/i18n/i18n';
import { updateUserSettings } from '@/services';
import type { LanguageCode } from '@/types';

export const DEFAULT_LANGUAGE: LanguageCode = 'en';
export const LANGUAGE_LIST: LanguageCode[] = ['en', 'da', 'ro', 'hu'];

export interface LanguageContextValue {
  language: LanguageCode;
  /** Switches the active language immediately and persists it for the signed-in user. */
  setLanguage: (code: LanguageCode) => void;
}

export const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

/**
 * Owns the active UI language: defaults to the signed-in user's saved
 * `language` preference (falling back to English for signed-out users or
 * accounts predating this feature), and applies it via `i18next.changeLanguage`.
 * Mirrors `ThemeProvider`'s load/override/persist pattern.
 */
export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const { appUser, firebaseUser } = useAuth();
  const { t } = useTranslation();
  const [language, setLanguageState] = useState<LanguageCode>(DEFAULT_LANGUAGE);

  useEffect(() => {
    if (appUser?.language && LANGUAGE_LIST.includes(appUser.language)) {
      setLanguageState(appUser.language);
    } else if (!firebaseUser) {
      setLanguageState(DEFAULT_LANGUAGE);
    }
  }, [appUser?.language, firebaseUser]);

  useEffect(() => {
    void i18n.changeLanguage(language);
  }, [language]);

  const setLanguage = useCallback(
    (code: LanguageCode) => {
      setLanguageState(code);
      if (firebaseUser) {
        updateUserSettings(firebaseUser.uid, { language: code }).catch(() => {
          notifications.show({ color: 'red', message: t('profile.errors.saveFailed') });
        });
      }
    },
    [firebaseUser, t],
  );

  const contextValue = useMemo<LanguageContextValue>(
    () => ({ language, setLanguage }),
    [language, setLanguage],
  );

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
};
